import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { ownerAPI } from '../../api/finance/owner';
import i18n from '../../i18n';

// Apply the owner's stored language preference to the UI. Without this, the
// preference saved in Settings was dead code: every login/refresh fell back to
// the browser locale (an EN owner with a FR browser saw French forever).
const applyOwnerLanguage = (profile) => {
  try {
    if (profile?.language && i18n.language !== profile.language) {
      i18n.changeLanguage(profile.language);
    }
  } catch { /* language sync must never break auth */ }
};

// Owner auth context
const OwnerAuthContext = createContext(null);

// Owner auth provider
export const OwnerAuthProvider = ({ children }) => {
  const [owner, setOwner] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check token on load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('owner-token');
      const savedOwner = localStorage.getItem('owner-data');

      if (token && savedOwner) {
        try {
          // Verify token is still valid
          const { data } = await ownerAPI.getMe();
          applyOwnerLanguage(data);
          setOwner(data);
          setIsAuthenticated(true);
        } catch (error) {
          // Invalid token, clean up
          localStorage.removeItem('owner-token');
          localStorage.removeItem('owner-data');
          setOwner(null);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    try {
      const { data } = await ownerAPI.login(email, password);

      // Save token first (needed for getMe auth)
      localStorage.setItem('owner-token', data.token);
      setIsAuthenticated(true);

      // Fetch full profile with populated unitIds
      const { data: fullProfile } = await ownerAPI.getMe();
      localStorage.setItem('owner-data', JSON.stringify(fullProfile));
      applyOwnerLanguage(fullProfile);
      setOwner(fullProfile);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login error';
      return { success: false, error: message };
    }
  }, []);

  // Accept invitation
  const acceptInvite = useCallback(async (token, password) => {
    try {
      const { data } = await ownerAPI.acceptInvite(token, password);

      // Save token first (needed for getMe auth)
      localStorage.setItem('owner-token', data.token);
      setIsAuthenticated(true);

      // Fetch full profile with populated unitIds
      const { data: fullProfile } = await ownerAPI.getMe();
      localStorage.setItem('owner-data', JSON.stringify(fullProfile));
      applyOwnerLanguage(fullProfile);
      setOwner(fullProfile);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Error activating account';
      return { success: false, error: message };
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem('owner-token');
    localStorage.removeItem('owner-data');
    setOwner(null);
    setIsAuthenticated(false);
  }, []);

  // Refresh profile
  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await ownerAPI.getMe();
      setOwner(data);
      localStorage.setItem('owner-data', JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Error refresh profile:', error);
      return null;
    }
  }, []);

  const value = {
    owner,
    isLoading,
    isAuthenticated,
    login,
    logout,
    acceptInvite,
    refreshProfile,
  };

  return (
    <OwnerAuthContext.Provider value={value}>
      {children}
    </OwnerAuthContext.Provider>
  );
};

// Hook to use owner auth
export const useOwnerAuth = () => {
  const context = useContext(OwnerAuthContext);

  if (!context) {
    throw new Error('useOwnerAuth must be used within an OwnerAuthProvider');
  }

  return context;
};

export default useOwnerAuth;
